import { useEffect, useState } from 'react';
import axios from 'axios';
import { TrendingUp, ArrowUp, ArrowRight } from 'lucide-react';
import TrendingTripsSheet from './TrendingTripsSheet';

import { getBackendUrl } from '../../lib/runtimeConfig';

const API = `${getBackendUrl()}/api`;

const formatNumber = (n) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '0';
  return n.toLocaleString('en-US');
};

/**
 * Social-proof banner under the AI Itinerary hero card. Designed to be
 * impossible to miss — bright gold-accented card that screams "you're not
 * alone, real people use this." Polls `/api/ai-search/stats` every 45s.
 *
 * The whole card is clickable — opens the TrendingTripsSheet so users can
 * browse the community's most-upvoted trips. This is the viral / social
 * proof loop that turns the planner from a single-player tool into a
 * community surface. Previously this was a tiny grey pill at the bottom
 * that users missed — the redesign promotes it to a hero-adjacent CTA.
 */
const TripsPlannedCounter = () => {
  const [count, setCount] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/ai-search/stats`, { timeout: 8000 });
        if (cancelled) return;
        // Prefer new `total_trips` field; fall back to legacy `trips_this_month`
        // so older backend deployments still render a number.
        const n = res?.data?.total_trips ?? res?.data?.trips_this_month;
        if (typeof n === 'number') setCount(n);
      } catch {
        // silently fail — counter is non-critical
      }
    };

    fetchStats();
    const t = setInterval(fetchStats, 45000); // refresh every 45s
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, []);

  // Always render — even with 0 trips — so the section is never missing.
  // Show a friendly "be the first" state when count is 0.
  const safeCount = typeof count === 'number' ? count : 0;
  const hasTrips = safeCount > 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setSheetOpen(true)}
        className="group/counter mt-4 w-full flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-3.5 rounded-xl bg-gradient-to-r from-gold-500/15 via-gold-400/10 to-wine-600/15 border border-gold-400/40 hover:border-gold-300/80 hover:from-gold-500/25 hover:to-wine-600/25 transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gold-500/20 text-left"
        data-testid="trips-planned-counter"
        aria-live="polite"
        title="Browse the top-10 trending trips leaderboard"
      >
        {/* Upvote-style chip on the left — communicates "vote-driven" at a glance */}
        <div className="shrink-0 inline-flex flex-col items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-lg bg-gold-500/20 border border-gold-400/50 group-hover/counter:bg-gold-500/30 transition-colors">
          <ArrowUp className="w-4 h-4 sm:w-5 sm:h-5 text-gold-300" strokeWidth={2.5} />
          <span className="text-[9px] sm:text-[10px] font-bold text-gold-200 leading-none tabular-nums">
            {hasTrips ? formatNumber(safeCount) : 'NEW'}
          </span>
        </div>

        {/* Middle copy block */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em] text-gold-300 mb-0.5">
            <span className="relative flex h-1.5 w-1.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-60" />
              <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
            </span>
            <TrendingUp className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
            Top 10 · Trending trips
          </div>
          <div className="text-sm sm:text-base font-semibold text-white leading-tight">
            {hasTrips ? (
              <>
                <span className="text-gold-200">{formatNumber(safeCount)}</span> trip{safeCount === 1 ? '' : 's'} planned by the community
              </>
            ) : (
              <>Be the <span className="text-gold-200">first</span> to plan a trip</>
            )}
          </div>
          <div className="text-[11px] sm:text-xs text-white/60 mt-0.5">
            {hasTrips ? 'Vote for the ones you love — top voted rise to the top →' : 'Yours will show up here for everyone to see →'}
          </div>
        </div>

        {/* Always-visible CTA arrow on the right */}
        <div className="shrink-0 inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider text-gold-300 group-hover/counter:text-gold-200 transition-colors">
          <span className="hidden sm:inline">See top 10</span>
          <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover/counter:translate-x-0.5" />
        </div>
      </button>

      <TrendingTripsSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </>
  );
};

export default TripsPlannedCounter;
